import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { BreadCrumbs } from '@/components/BreadCrumbs/BreadCrumbs'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import {
  ApiariesProvider,
  type DeploymentLabelOption,
  useApiaries,
} from '@/contexts/useApiaries'
import {
  ClusterRevisionForm,
  type ClusterRevisionFormType,
  RevisionFormMode,
  getSystemConfigWithReadonlyFields,
  getSystemConfigWithoutReadonlyFields,
} from '@/features/ClusterConfigs/components/ClusterRevisionForm/ClusterRevisionForm'
import { getClusterRevisionFormSchema } from '@/features/ClusterConfigs/components/ClusterRevisionForm/schema'
import { SubmitWithCommentPopover } from '@/features/ClusterConfigs/components/SubmitWithCommentPopover/SubmitWithCommentPopover'
import {
  useGetClusterRevisionById,
  usePutCluster,
} from '@/generated/http-clients/v3ApiComponents'
import type { ClusterRevision } from '@/generated/http-clients/v3ApiSchemas'
import { toaster } from '@/hooks/useToast/toastUtils'
import { router } from '@/router'
import { Skeleton } from '@chakra-ui/react'
import { useForm } from '@tanstack/react-form'
import type { UseQueryResult } from '@tanstack/react-query'
import { createRoute, useNavigate } from '@tanstack/react-router'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { clusterConfigTableRoute } from '../ClusterConfigTable/ClusterConfigTable'
import { clusterConfigsRootRoute } from '../ClusterConfigsPage/ClusterConfigsPage'

export const clusterConfigDraftRoute = createRoute({
  getParentRoute: () => clusterConfigsRootRoute,
  path: '$baseRevisionId/draft',
  component: () => (
    <ApiariesProvider>
      <ClusterConfigDraftPage />
    </ApiariesProvider>
  ),
})

const getRevisionDraftSchema = (t: TFunction) =>
  getClusterRevisionFormSchema(t).extend({
    comment: z.string().min(1, String(t('translation:validation.required'))),
  })

type FormValues = z.infer<ReturnType<typeof getRevisionDraftSchema>>

export function ClusterConfigDraftPage() {
  const pathParams = clusterConfigDraftRoute.useParams()
  const { t } = useTranslation(['features', 'translation'], {
    useSuspense: false,
  })

  const { deploymentLabels, isLoading: isLoadingApiaries } = useApiaries()
  const baseRevision = useGetClusterRevisionById({
    pathParams: { ...pathParams, revisionId: pathParams.baseRevisionId },
  })

  const isLoading = baseRevision.isLoading || isLoadingApiaries
  const isError = baseRevision.isError && !baseRevision.isFetching
  const isRevisionFetched = baseRevision.isSuccess && baseRevision.data !== null

  if (isLoading || isError || !isRevisionFetched) {
    return (
      <PageLayout
        beforeTitle={<BreadCrumbs paths={getBreadcrumbs(t, pathParams)} />}
        title={t('clusterConfigDraft.pageTitle')}
      >
        {isLoading && <Skeleton h="400px" w="100%" />}

        {(isError || !isRevisionFetched) && (
          <ApiErrorState
            apiResource={baseRevision as unknown as UseQueryResult}
            errorTitle={t('clusterConfigDraft.errors.loadingTitle')}
            errorDescription={t('clusterConfigDraft.errors.loadingDescription')}
          />
        )}
      </PageLayout>
    )
  }

  if (isRevisionFetched) {
    return (
      <ClusterConfigDraft
        baseRevision={baseRevision.data}
        deploymentLabels={deploymentLabels}
      />
    )
  }
}

function ClusterConfigDraft({
  baseRevision,
  deploymentLabels,
}: Readonly<{
  baseRevision: ClusterRevision
  deploymentLabels: DeploymentLabelOption[]
}>) {
  const pathParams = clusterConfigDraftRoute.useParams()
  const navigate = useNavigate()
  const { t } = useTranslation(['features', 'translation'], {
    useSuspense: false,
  })

  const createClusterConfigRevision = usePutCluster()

  const submitNewRevision = async (configState: FormValues) => {
    const { comment, ...config } = configState

    try {
      await createClusterConfigRevision.mutateAsync({
        body: {
          product_id: config.productId,
          template_id: config.templateId,
          system: getSystemConfigWithReadonlyFields(config.system, {
            name: baseRevision.hive_name,
            deployment_label: baseRevision.deployment_label,
          }),
          override: config.override,
          customer: config.customer,
          changelog: comment,
        },
        pathParams,
      })
      handleRevisionSubmitSuccess()
    } catch (error: unknown) {
      console.error('error posting', error)
      toaster.create({
        title: t('clusterConfigDraft.toast.createError'),
        type: 'error',
        duration: 5000,
      })
    }
  }

  const systemConfigWithoutReadonlyFields =
    getSystemConfigWithoutReadonlyFields(baseRevision.system)

  const form = useForm({
    defaultValues: {
      name: baseRevision.hive_name,
      organizationId: baseRevision.org_id,
      deploymentLabel:
        baseRevision.deployment_label ||
        baseRevision.system?.global?.deployment_label ||
        deploymentLabels[0]?.value ||
        '',
      templateId: baseRevision.template_id || '',
      productId: baseRevision.product_id,
      system: systemConfigWithoutReadonlyFields,
      customer: baseRevision.customer ?? {},
      override: baseRevision.override ?? {},
      _system_raw_json: JSON.stringify(
        systemConfigWithoutReadonlyFields,
        null,
        2,
      ),
      _override_raw_json: JSON.stringify(baseRevision.override ?? {}, null, 2),
      _customer_raw_json: JSON.stringify(baseRevision.customer ?? {}, null, 2),
      comment: '',
    } as FormValues,
    validators: {
      onChange: getRevisionDraftSchema(t),
    },
    onSubmit: ({ value }) => submitNewRevision(value),
  })

  const handleRevisionSubmitSuccess = () => {
    toaster.create({
      title: t('clusterConfigDraft.toast.createSuccess'),
      type: 'success',
      duration: 2000,
    })
    navigate({
      to: clusterConfigsRootRoute.to,
      params: pathParams,
    })
  }

  return (
    <PageLayout
      beforeTitle={
        <BreadCrumbs
          paths={getBreadcrumbs(
            t,
            pathParams,
            baseRevision.system?.global?.name,
          )}
        />
      }
      title={t('clusterConfigDraft.pageTitle')}
      action={
        <form.Field name="comment">
          {(field) => (
            <SubmitWithCommentPopover
              onSubmit={form.handleSubmit}
              comment={field.state.value}
              commentError={
                field.state.meta.isTouched
                  ? field.state.meta.errors[0]?.message
                  : undefined
              }
              isReadyToSubmit={form.state.canSubmit}
              onCommentChange={field.handleChange}
              translations={{
                buttonText: t('clusterConfigDraft.actions.reviewChanges'),
                popoverTitle: t('clusterConfigDraft.submitPopover.title'),
                commentPlaceholder: t(
                  'clusterConfigDraft.submitPopover.commentPlaceholder',
                ),
                submitButtonText: t('translation:actions.submit'),
                invalidConfigTooltip: t(
                  'clusterConfigDraft.validation.invalidConfig',
                ),
              }}
            />
          )}
        </form.Field>
      }
    >
      <ClusterRevisionForm
        mode={RevisionFormMode.REVISION_DRAFT}
        // Type casting because TanstackForm has terrible TS public types
        form={form as unknown as ClusterRevisionFormType}
      />
    </PageLayout>
  )
}

function getBreadcrumbs(
  t: TFunction,
  pathParams: {
    orgId: string
    clusterId: string
    baseRevisionId: string
  },
  clusterName?: string,
) {
  return [
    {
      name: t('translation:sidebar.v3ClusterRevisions'),
      path: clusterConfigTableRoute.to,
    },
    {
      name: clusterName || pathParams.clusterId,
      path: router.buildLocation({
        to: clusterConfigsRootRoute.to,
        params: pathParams,
      }).href,
    },
    { name: t('clusterConfigDraft.breadcrumbs.newRevision'), path: '' },
  ]
}
